/**
 * Helpers for navigating to / building the Access Denied (403) page with a
 * stable destination page reference.
 *
 * Vue Router 4 discards `params` that are not declared in the route path.
 * The 403 route is `/restricted` (no dynamic segments), so we pass only the
 * page reference via `query.deniedPath`.
 */

/** @type {import('vue-router').RouteLocationNormalized|null} */
let pendingNavigationRoute = null;

const MAX_DENIED_PATH_LENGTH = 256;

/** Safe path characters only — reject phishing-style free text in deniedPath. */
const VALID_DENIED_PATH = /^\/[\w\-./%~]*$/;

/**
 * Normalize a route path for display. Preserve "/" (home) instead of
 * collapsing it to an empty string after stripping trailing slashes.
 *
 * @param {unknown} path
 * @returns {string|null}
 */
export function normalizeDeniedPath(path) {
    if (path == null) {
        return null;
    }

    const asString = String(path).trim();

    if (!asString || asString.startsWith('//') || asString === '/restricted') {
        return null;
    }

    if (asString.length > MAX_DENIED_PATH_LENGTH) {
        return null;
    }

    const withoutTrailingSlash = asString.replace(/\/+$/, '');

    // Home / dashboard resolves to "/" — do not erase it.
    const normalized = withoutTrailingSlash === '' ? '/' : withoutTrailingSlash;

    if (!VALID_DENIED_PATH.test(normalized)) {
        return null;
    }

    return normalized;
}

/**
 * Remember the route a navigation is trying to reach (cleared in afterEach).
 * Used when a 403 axios response fires while guards are still resolving.
 *
 * @param {import('vue-router').RouteLocationNormalized|null} route
 */
export function setPendingNavigationRoute(route) {
    pendingNavigationRoute = route ?? null;
}

/**
 * Resolve the route the user was trying to reach for Access Denied attribution.
 *
 * @param {{ currentRoute?: { value?: object }|null }} router
 * @returns {object|null}
 */
export function resolveIntendedRouteForAccessDenied(router) {
    if (pendingNavigationRoute?.path) {
        return pendingNavigationRoute;
    }

    return router?.currentRoute?.value ?? null;
}

/**
 * Resolve the page reference to show on Access Denied from props.
 *
 * @param {{ deniedPath?: string }} props
 * @returns {string|null}
 */
export function resolveDeniedPageReference(props = {}) {
    return normalizeDeniedPath(props.deniedPath);
}

/** Resolve display-only details from registered route metadata and the current origin.
 * Query strings and fragments are intentionally excluded from access-denied references.
 */
export function resolveDeniedPageDetails(router, path, origin) {
    const deniedPath = normalizeDeniedPath(path);
    if (!deniedPath || !router || !origin) {
        return null;
    }

    try {
        const route = router.resolve({ path: deniedPath });
        const name = route.meta?.title;
        const url = new URL(deniedPath, origin);
        if (
            !route.matched?.length ||
            ['403', '404'].includes(route.name) ||
            typeof name !== 'string' ||
            !name.trim() ||
            url.origin !== origin
        ) {
            return null;
        }

        return { name: name.trim(), url: url.href };
    } catch {
        return null;
    }
}

/**
 * Build a router location for the Access Denied page.
 * Only `deniedPath` / `fromPath` are carried in the URL so copy stays on i18n keys in NoAccess.
 *
 * @param {object|null} intended - the route the user was trying to reach
 * @param {object|null} [from] - optional previous route for the Back button
 * @returns {{ name: string, query: object }}
 */
export function buildAccessDeniedLocation(intended = null, from = null) {
    const query = {};
    const deniedPath = normalizeDeniedPath(intended?.path ?? intended?.fullPath);
    const fromPath = normalizeDeniedPath(from?.path ?? from?.fullPath);

    if (deniedPath) {
        query.deniedPath = deniedPath;
    }

    if (fromPath && fromPath !== deniedPath) {
        query.fromPath = fromPath;
    }

    return {
        name: '403',
        query,
    };
}
